// Central API configuration for the Laravel backend
export const API_BASE = '/api';

// Get the JWT token from localStorage
export const getToken = () => localStorage.getItem('authToken');

// Logout helper - clears token and redirects to home
export const handleLogout = () => {
  localStorage.removeItem('authToken');
  localStorage.removeItem('loggedUser');
  window.location.href = '/';
};

// Wrapper for fetch that handles 401 responses
const fetchWithAuth = async (url, options) => {
  const response = await fetch(url, options);
  
  // If token expired or invalid, logout automatically
  if (response.status === 401) {
    const data = await response.json();
    console.error('Authentication failed:', data.message);
    alert('Η συνεδρία σας έληξε. Παρακαλώ συνδεθείτε ξανά.');
    handleLogout();
    throw new Error('Token expired');
  }
  
  return response;
};

// Returns headers for authenticated (JWT-protected) requests
export const authHeaders = () => {
  const token = getToken();
  console.log('Auth token:', token ? `${token.substring(0, 20)}...` : 'NO TOKEN');
  return {
    'Authorization': 'Bearer ' + token,
    'Content-Type': 'application/json',
  };
};

// Returns headers for public requests
export const jsonHeaders = () => ({
  'Content-Type': 'application/json',
});

// --- Auth ---

export const apiLogin = async (username, password) => {
  const response = await fetch(`${API_BASE}/login`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({ username, password }),
  });
  
  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.message || 'Login failed');
  }
  
  return data;
};

export const apiRegister = async (username, email, password) => {
  const response = await fetch(`${API_BASE}/register`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({ username, email, password }),
  });
  
  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.message || 'Registration failed');
  }
  
  return data;
};

export const apiReverify = (token) =>
  fetch(`${API_BASE}/reverify`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({ token }),
  }).then(r => r.json());

export const apiValidateToken = () =>
  fetchWithAuth(`${API_BASE}/validate-token`, {
    method: 'POST',
    headers: authHeaders(),
  }).then(r => r.json());

// --- Questions ---

export const apiGetQuestion = (type, id) =>
  fetch(`${API_BASE}/questions/${type}/${id}`, {
    method: 'GET',
    headers: jsonHeaders(),
  }).then(r => r.json());

// --- Comments ---

export const apiGetComments = () =>
  fetch(`${API_BASE}/comments`, {
    method: 'GET',
    headers: jsonHeaders(),
  }).then(r => r.json());

export const apiPostComment = (username, comment, timestamp) =>
  fetchWithAuth(`${API_BASE}/comments`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ username, comment, timestamp }),
  }).then(r => r.json());

// --- Scoring ---

export const apiScoreText = (userAnswer, correctAnswer) =>
  fetch(`${API_BASE}/score/text`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({ userAnswer, correctAnswer }),
  }).then(r => r.json());

export const apiScoreCode = (userCode, correctCode) =>
  fetch(`${API_BASE}/score/code`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({ userCode, correctCode }),
  }).then(r => r.json());

export const apiCodeSuggestions = (userCode, correctCode) =>
  fetch(`${API_BASE}/score/suggestions`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({ userCode, correctCode }),
  }).then(r => r.json());

export const apiNlpSimilarity = (string1, string2) =>
  fetch(`${API_BASE}/nlp/similarity`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({ string1, string2 }),
  }).then(r => r.json());

// --- Exam ---

export const apiGenerateExam = () =>
  fetchWithAuth(`${API_BASE}/exam/generate`, {
    method: 'POST',
    headers: authHeaders(),
  }).then(r => r.json());

export const apiSubmitExam = (examToken, answers) =>
  fetchWithAuth(`${API_BASE}/exam/submit`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ examToken, answers }),
  }).then(r => r.json());

// --- Results ---

export const apiSaveResults = (loggedUser, totalScore, serverData) =>
  fetchWithAuth(`${API_BASE}/results`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ loggedUser, totalScore, serverData }),
  }).then(r => r.json());

// --- Statistics ---

export const apiGetStatistics = (username) =>
  fetchWithAuth(`${API_BASE}/statistics`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ username }),
  }).then(r => r.json());

// --- User Settings ---

export const apiUserSettings = (payload) =>
  fetchWithAuth(`${API_BASE}/user/settings`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(payload),
  }).then(r => r.json());

// --- Health ---

export const apiPing = () =>
  fetch(`${API_BASE}/ping`, {
    method: 'POST',
    headers: jsonHeaders(),
  }).then(r => r.json());
